import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  isArticleLoadFailureRetryable,
  type ArticleLoadFailureReason,
} from '../articleLoadFailure';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';

interface ArticleSheetErrorProps {
  reason: ArticleLoadFailureReason;
  isRetrying: boolean;
  onRetry: () => void;
  onClose: () => void;
  onSignIn: () => void;
}

// Only what we can actually stand behind. A 404 does not prove the article was deleted or
// belongs to another Agent -- in the production failure this panel was built for, the row
// existed and was owned by the scenario in the URL -- so it names no cause. A 401 with the
// token still in place says nothing about the session either, and reuses the neutral message;
// only a cleared token earns the app's existing session-expired copy.
const MESSAGE_KEYS: Record<ArticleLoadFailureReason, string> = {
  not_found: 'knowledge_base:article.load_error_not_found',
  forbidden: 'knowledge_base:article.load_error_forbidden',
  session_expired: 'auth:session_expired',
  auth_refresh_failed: 'knowledge_base:article.load_error',
  bad_request: 'knowledge_base:article.load_error',
  backend_error: 'knowledge_base:article.load_error',
  network_error: 'knowledge_base:article.load_error',
};

export const ArticleSheetError = ({
  reason,
  isRetrying,
  onRetry,
  onClose,
  onSignIn,
}: ArticleSheetErrorProps) => {
  const { t } = useTranslation(['knowledge_base', 'auth', 'login']);
  const canRetry = isArticleLoadFailureRetryable(reason);

  return (
    <div className="flex h-full items-center justify-center py-8" data-testid="article-sheet-error">
      <Alert variant="destructive" className="max-w-md">
        <AlertCircle className="size-4 !text-destructive" />
        <AlertDescription className="space-y-3">
          <p className="m-0" data-testid="article-sheet-error-message">
            {t(MESSAGE_KEYS[reason])}
          </p>
          {/* A 4xx resolves the same way every time, so the only honest control is a way out.
              `loading` also disables the button, so an in-flight retry cannot be double-fired
              and the click storm this panel is named for cannot repeat. */}
          {canRetry ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={isRetrying}
              onClick={onRetry}
              data-testid="article-sheet-retry"
            >
              {t('knowledge_base:article.retry')}
            </Button>
          ) : reason === 'session_expired' ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onSignIn}
              data-testid="article-sheet-sign-in"
            >
              {t('login:button.login')}
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onClose}
              data-testid="article-sheet-back"
            >
              {t('knowledge_base:article.back_to_knowledge_base')}
            </Button>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
};
